import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  FlexBox,
  Grid,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { actions as clientsActions, selectors as clientsSelectors, Client } from 'store/clients';
import { getSortedList } from 'utils/stringFormat';
import { CreateCustomerOverlay } from './components/CustomerCreateOverlay/CreateCustomerOverlay';
import { CustomerEditDrawer } from './components/CustomerEditDrawer';
import { useStyles } from './styled';

const CustomersView: React.FC = () => {
  const { deleteClient, getClients, resetAction, setClientId } = useActions({
    deleteClient: clientsActions.deleteClient,
    getClients: clientsActions.getClients,
    resetAction: clientsActions.resetAction,
    setClientId: clientsActions.setClientId
  });

  const authAdmin = useSelector(authSelectors.selectAdmin);
  const { success: clientSuccess, error: clientError } = useSelector(clientsSelectors.selectAction);
  const activeClient = useSelector(clientsSelectors.selectClient) as Client;
  const clients = useSelector(clientsSelectors.selectClients) as Client[];

  const [showCreateCustomerOverlay, setShowCreateCustomerOverlay] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const classes = useStyles()();

  useEffect(() => {
    getClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clientSuccess) {
      resetAction();
      setShowCreateCustomerOverlay(false);
      setClientToEdit(null);
      setClientToDelete(null);

      getClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSuccess]);

  useEffect(() => {
    if (clientError) {
      if (clientToDelete) {
        setDeleteError(`${clientToDelete.name} cannot be deleted at this time.`);
        setClientToDelete(null);
      } else {
        setShowCreateCustomerOverlay(false);
        setClientToEdit(null);
      }

      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientError]);

  const onDeleteConfirmClick = () => {
    if (clientToDelete) {
      deleteClient({ id: clientToDelete.id });
    }
  };

  return (
    <>
      {deleteError && (
        <ActionOverlay
          title={
            <Typography fontWeight={600} className={classes.customerDeleteHeader}>
              Delete Failed
            </Typography>
          }
          description={<Typography className={classes.customerDeleteDescriptionText}>{deleteError}</Typography>}
          confirmText="OK"
          onConfirm={() => {
            setDeleteError(null);
            resetAction();
          }}
          width={800}
          height={500}
          appearance="error"
        />
      )}

      {clientToDelete && (
        <ActionOverlay
          title={
            <MuiBox className={classes.customerDeleteHeading}>
              <Typography className={classes.customerDeleteHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
                Are you sure you want to delete {clientToDelete?.name}?
              </Typography>
            </MuiBox>
          }
          description={
            <MuiBox className={classes.customerDeleteDescription}>
              <Typography className={classes.customerDeleteDescriptionText}>
                {clientToDelete?.name} will be deleted immediately.
              </Typography>
            </MuiBox>
          }
          confirmText="Delete"
          onConfirm={() => onDeleteConfirmClick()}
          cancelText="Back"
          onCancel={() => setClientToDelete(null)}
          width={800}
          appearance="error"
        />
      )}

      <Grid className={classes.customersHeaderBlock} data-testid="customers-page">
        <Box flexOverride="none">
          <Typography className={classes.customersHeading}>Customers</Typography>
        </Box>
        <Button
          customSize="small"
          width={175}
          className={classes.customerAddNewButton}
          onClick={() => setShowCreateCustomerOverlay(true)}
        >
          Create Customer
        </Button>
      </Grid>

      <MuiBox className={classes.customersTableOuterBox}>
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer id="userViewTable" className={classes.customersTableContainer}>
              <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse' }}>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell className={classes.customersTableName}>Name</MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.customersTableActionHeaders}>Actions</MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>

                <MuiTable.TableBody>
                  {getSortedList(clients, 'name').map((client) => {
                    const isActive = activeClient?.id === client.id;
                    const isAssignedClient = authAdmin?.clientId === client.id;

                    return (
                      <MuiTable.TableRow
                        key={client.id}
                        className={clientToEdit?.id === client.id ? classes.selectedRow : undefined}
                      >
                        <MuiTable.TableCell className={classes.customersTableName}>
                          <Typography fontSize={16}>{client.name}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <FlexBox justify="end" className={classes.actionIconsBox}>
                            {!isActive && (
                              <Tooltip title="Make Customer Active" arrow placement="top" verticalOffset={-8}>
                                <SvgIconButton
                                  onClick={() => setClientId(client.id)}
                                  data-testid="activate-customer-button"
                                >
                                  <SvgIcon name="check_circle" />
                                </SvgIconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit Customer" arrow placement="top" verticalOffset={-8}>
                              <SvgIconButton
                                onClick={() => setClientToEdit(client)}
                                data-testid="update-customer-button"
                              >
                                <SvgIcon name="edit_circle" />
                              </SvgIconButton>
                            </Tooltip>
                            {!isActive && !isAssignedClient && (
                              <Tooltip title="Delete Customer" arrow placement="top" verticalOffset={-8}>
                                <SvgIconButton
                                  onClick={() => setClientToDelete(client)}
                                  data-testid="delete-customer-button"
                                >
                                  <SvgIcon name="exit_circle" />
                                </SvgIconButton>
                              </Tooltip>
                            )}
                          </FlexBox>
                        </MuiTable.TableCell>
                      </MuiTable.TableRow>
                    );
                  })}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
      </MuiBox>

      <Grid className={classes.footer}>
        <p className={classes.total}>Customers: {clients.length}</p>
      </Grid>
      {showCreateCustomerOverlay && <CreateCustomerOverlay onClose={() => setShowCreateCustomerOverlay(false)} />}
      {clientToEdit && <CustomerEditDrawer isOpen client={clientToEdit} onClose={() => setClientToEdit(null)} />}
    </>
  );
};

export default CustomersView;
